/**
 * Utility functions for converting React Flow data to legacy format
 */
import { FIELD_NAME_MAPPINGS } from '../shared/constants/index'
import { generateOptionId } from './nodeConverters/utils'

/**
 * Map camelCase keys to snake_case keys for legacy compatibility
 */
export function convertKeyToSnakeCase(key: string, _nodeType: string): string {
  // Direct mappings for specific keys
  if (FIELD_NAME_MAPPINGS[key]) {
    return FIELD_NAME_MAPPINGS[key]
  }

  // Convert camelCase to snake_case for other keys
  // This handles keys like maxWaitTime -> max_wait_time
  return key.replace(/([A-Z])/g, '_$1').toLowerCase()
}

/**
 * Convert boolean to legacy string format ("1" or "0")
 */
export function booleanToString(value: boolean): string {
  return value ? '1' : '0'
}

/**
 * Convert null/undefined to legacy string format
 * Legacy sometimes uses string "null" or "undefined" instead of JSON null
 *
 * @param value - Value to convert
 * @param fieldName - Field name to determine conversion pattern
 * @returns String "null", "undefined", or the original value
 */
function convertNullToLegacyString(value: unknown, fieldName: string): unknown {
  // If value is null or undefined, convert to string based on field pattern
  if (value === null || value === undefined) {
    // Fields that use "undefined" string
    if (fieldName === 'lookup_id' || fieldName === 'new_caller_id_option') {
      return 'undefined'
    }
    // Fields that use "null" string
    if (
      fieldName === 'sound_clip_id' ||
      fieldName === 'extension_id' ||
      fieldName === 'queue_id'
    ) {
      return 'null'
    }
    // Most other fields use JSON null
    return null
  }
  return value
}

/**
 * Convert React Flow node data to legacy html format with snake_case keys
 */
/**
 * Convert React Flow node data to legacy HTML format
 *
 * CRITICAL: Data Type Conversion Strategy
 *
 * Boolean Fields:
 * - Most boolean fields → Numbers (0 or 1): minimized, press_1, auto_reply (for most nodes)
 * - SMS node boolean fields → Strings ("0" or "1"): minimized, auto_reply (SMS-specific)
 * - Uses booleanToLegacyNumber() for number conversion, booleanToString() for string conversion
 *
 * Numeric Fields:
 * - Timeouts, durations, repeatTimes → Strings: "30", "60", "2"
 * - IDs (extension_id, queue_id, etc.) → Strings: "123", "null"
 * - Some numeric fields remain as numbers (weight_id percentages)
 *
 * String Fields:
 * - Most string fields remain as strings
 * - Null/undefined values → String "null" or "undefined" (field-specific)
 *
 * Array Fields:
 * - children arrays for auto_attendant, call_distribution, interactive_webhook
 * - Must start with empty object [{}] as first element
 *
 * @param nodeData - React Flow node data object
 * @param nodeType - React Flow node type
 * @param nodeId - Optional node ID
 * @returns Legacy HTML format object with snake_case keys
 */
export function convertNodeDataToHtml(
  nodeData: Record<string, unknown>,
  nodeType: string,
  nodeId?: string,
): Record<string, unknown> {
  const html: Record<string, unknown> = {}

  Object.entries(nodeData).forEach(([key, value]) => {
    // Skip label as it's not part of html
    if (key === 'label') {
      return
    }

    // Skip Admin3-only metadata fields that legacy doesn't store
    // Per ADMIN_VS_LEGACY.md: mode, toggle_code, status, allow_extension_as_option
    if (
      key === 'mode' ||
      key === 'toggleCode' ||
      key === 'status' ||
      key === 'allowExtensionAsOption'
    ) {
      return
    }

    // Skip connection metadata fields - legacy does NOT store these in HTML
    // Connections are only stored in endpoints.sources[].target_uuid
    const connectionMetadataFields = [
      'onTimeout',
      'on_timeout',
      'whenOpen',
      'when_open',
      'whenClosed',
      'when_closed',
      'onInvalid',
      'on_invalid',
      'onEscape',
      'on_escape',
      'holiday',
      'noAnswer',
      'no_answer',
      'busy',
    ]
    if (connectionMetadataFields.includes(key)) {
      return
    }

    // Convert key to snake_case
    let snakeKey = convertKeyToSnakeCase(key, nodeType)

    // Special handling: direct_line uses line_id instead of extension_id
    if (nodeType === 'direct_line' && key === 'extensionId') {
      snakeKey = 'line_id'
    }

    // Special handling: interactive_webhook uses iwebhook_url instead of url
    if (nodeType === 'interactive_webhook' && key === 'url') {
      snakeKey = 'iwebhook_url'
    }

    // Special handling: interactive_webhook uses iwebhook_allow_extension_dialing instead of allowExtensionAsOption
    if (
      nodeType === 'interactive_webhook' &&
      key === 'allowExtensionAsOption'
    ) {
      snakeKey = 'iwebhook_allow_extension_dialing'
    }

    // Special handling: webhook uses notify_url instead of url
    if (nodeType === 'webhook' && key === 'url') {
      snakeKey = 'notify_url'
    }

    // Special handling: transfer_rollover uses transfer_rollover_id instead of extensionId
    if (nodeType === 'transfer_rollover' && key === 'extensionId') {
      snakeKey = 'transfer_rollover_id'
    }

    // Special handling: SMS uses sms_recipient instead of phoneNumber
    if (nodeType === 'sms_notification' && key === 'phoneNumber') {
      snakeKey = 'sms_recipient'
    }

    // Special handling: SMS uses sms_number instead of sendFrom
    if (nodeType === 'sms_notification' && key === 'sendFrom') {
      snakeKey = 'sms_number'
    }

    // Handle special cases
    if (key === 'messageType' && nodeType === 'sms_notification') {
      // Map messageType to option for SMS nodes
      // "Missed call" -> "1", "Incoming call" -> "2", "Voicemail" -> "3", "Custom" -> "4"
      if (value === 'Missed call') {
        html['option'] = '1'
      } else if (value === 'Incoming call') {
        html['option'] = '2'
      } else if (value === 'Voicemail') {
        html['option'] = '3'
      } else if (value === 'Custom') {
        html['option'] = '4'
      }
      return
    }

    // Handle special boolean field mappings
    if (key === 'requireOneToConnect' && nodeType === 'external_number') {
      // For external_number, requireOneToConnect maps to press_1
      // Legacy uses strings ("1"/"0"), not numbers
      html['press_1'] = typeof value === 'boolean' ? (value ? '1' : '0') : '0'
      return
    }

    // Handle boolean values that need to be strings ("0"/"1") for legacy
    // Legacy uses strings for: minimized (sms), auto_reply (sms)
    // Legacy uses numbers for: minimized (change_caller_id), press_1 (external_number)
    if (typeof value === 'boolean') {
      if (key === 'minimized' && nodeType === 'sms_notification') {
        // sms uses string "0"/"1" for minimized
        html[snakeKey] = value ? '1' : '0'
        return
      } else if (
        key === 'autoReplyToCaller' &&
        nodeType === 'sms_notification'
      ) {
        // sms uses string "0"/"1" for auto_reply
        html[snakeKey] = value ? '1' : '0'
        return
      } else if (
        key === 'press1' ||
        (key === 'requireOneToConnect' && nodeType === 'external_number')
      ) {
        // external_number uses string "0"/"1" for press_1 (handled separately)
        // This case is already handled above for requireOneToConnect
      } else if (
        key === 'autoReplyToCaller' ||
        key === 'minimized' ||
        key === 'press1'
      ) {
        // Other nodes (including change_caller_id) use numbers (0/1) for these fields
        html[snakeKey] = value ? 1 : 0
        return
      }
    }

    // Handle webhook URL mapping - url maps to notify_url (not iwebhook_url)
    if (nodeType === 'webhook' && key === 'url' && value) {
      html['notify_url'] = String(value)
      return // Don't process url field further
    }

    // Handle number values - keep as numbers for minimized, press_1, auto_reply
    // Legacy uses numbers (0/1) for these fields
    if (
      typeof value === 'number' &&
      (key === 'minimized' || key === 'press1' || key === 'autoReply')
    ) {
      html[snakeKey] = value
      return
    }

    // Handle numeric values that should be strings (timeouts, durations, etc.)
    if (
      typeof value === 'number' &&
      (key === 'timeout' ||
        key === 'duration' ||
        key === 'repeatTimes' ||
        key.includes('Id'))
    ) {
      html[snakeKey] = String(value)
      return
    }

    // Handle arrays (children for call_distribution, auto_attendant, interactive_webhook)
    if (Array.isArray(value) && key === 'options') {
      // Convert options array to children array format
      const children: Array<Record<string, unknown>> = [{}] // First element is always empty object
      value.forEach((option, index) => {
        const child: Record<string, unknown> = {}

        if (nodeType === 'auto_attendant' || nodeType === 'call_distribution') {
          // For auto_attendant: digit -> press_id
          // For call_distribution: weight -> weight_id
          if ('digit' in option && option.digit) {
            child['press_id'] = String(option.digit)
          }
          if ('weight' in option && option.weight !== undefined) {
            child['weight_id'] = String(option.weight)
          }
          // Generate option ID if not present
          // CRITICAL: Use generateOptionId() to ensure consistent format matching Legacy
          // This preserves existing option IDs and generates new ones in correct format
          if (!child['id']) {
            const existingId =
              option && typeof option === 'object' && 'id' in option
                ? String(option.id)
                : undefined
            child['id'] = generateOptionId(nodeId, index, existingId)
          }
        } else if (nodeType === 'interactive_webhook') {
          // For interactive_webhook: value -> option_id
          if ('value' in option && option.value) {
            child['option_id'] = String(option.value)
          }
          // Generate option ID if not present
          // CRITICAL: Use generateOptionId() to ensure consistent format matching Legacy
          // This preserves existing option IDs and generates new ones in correct format
          if (!child['id']) {
            const existingId =
              option && typeof option === 'object' && 'id' in option
                ? String(option.id)
                : undefined
            child['id'] = generateOptionId(nodeId, index, existingId)
          }
        }

        // CRITICAL: Store target node ID from option-{index} field in nodeData
        // This ensures round-trip compatibility (Admin3 → Legacy → Admin3)
        const optionKey = `option-${index}`
        if (nodeData[optionKey] && typeof nodeData[optionKey] === 'string') {
          child['target'] = String(nodeData[optionKey])
        }

        children.push(child)
      })
      html['children'] = children
      return
    }

    // Handle option-{index} fields (e.g., option-0, option-1) - store as-is
    // These are target node IDs for dynamic handles
    if (key.startsWith('option-') && typeof value === 'string') {
      html[key] = String(value)
      return
    }

    // Convert null/undefined to legacy string format if needed
    const convertedValue = convertNullToLegacyString(value, snakeKey)

    // Special handling for line_id (direct_line) - should use null string format like extension_id
    if (snakeKey === 'line_id') {
      if (convertedValue === null || convertedValue === undefined) {
        html[snakeKey] = 'null'
      } else {
        html[snakeKey] = String(convertedValue)
      }
      return
    }

    // Special handling for iwebhook_url (interactive_webhook) - should use null if empty
    if (snakeKey === 'iwebhook_url') {
      if (
        convertedValue === null ||
        convertedValue === undefined ||
        convertedValue === ''
      ) {
        html[snakeKey] = null
      } else {
        html[snakeKey] = String(convertedValue)
      }
      return
    }

    // Default: convert key and use value as-is (but ensure strings for IDs)
    if (snakeKey.endsWith('_id') && typeof convertedValue === 'string') {
      html[snakeKey] = convertedValue
    } else {
      html[snakeKey] = convertedValue
    }
  })

  return html
}
